import React, { createContext, useContext, useReducer, useEffect } from 'react';

import Axios from 'axios';
import _ from 'lodash';

import ls from 'local-storage';

import { EVENTS_FILTERS } from '@configs/Filters/EVENTS_FILTERS';

import { getMoment } from '@libs/NwMoment';

import { findFilterInFiltersBySection } from '@components/Filters/FilterForm/FilterUtils';

const EventsSearchContext = createContext(null);
const EventsSearchDispatchContext = createContext(null);

// Helper function to save smart lists to backend
const saveEventsSmartList = async (customlists) => {
    const onlycustoms = customlists
        .filter((item) => !item.fixed)
        .map((item) => {
            return {
                id: item.id,
                name: item.name,
                filters: item.filters,
                fixed: item.fixed,
            };
        });
    try {
        await Axios.post('/userclientconfigurations', {
            Name: 'event-smart-list',
            JsonData: JSON.stringify({ smartList: onlycustoms }),
        });
    } catch (error) {
        console.error('Save Events Smart List Error => ', error);
    }
};

// Helper function to format filter values (replace placeholders)
const formatEventFilterValues = (filters, currentUserID) => {
    return filters.map((subFilter) => {
        const filterItem = EVENTS_FILTERS.filters.find((item) => item.name === subFilter.name);
        if (filterItem) {
            if (filterItem.type === 'date' || filterItem.type === 'period') {
                let value = subFilter.value;
                if (subFilter.value && typeof subFilter.value === 'string') {
                    const match = subFilter.value.match(/\{(\w+)\}/);
                    if (!match) {
                        value = getMoment(subFilter.value);
                    }
                }
                return {
                    ...subFilter,
                    value: value,
                };
            }
            if (filterItem.type === 'booker-selector') {
                if (subFilter.value === '{me}') {
                    return {
                        ...subFilter,
                        value: currentUserID,
                    };
                }
            }
        }
        return subFilter;
    });
};

// Helper function to hydrate active filters with full filter definitions
const getHydratedActiveFilters = (activefilters, fulllist) => {
    const hafs = [];
    if (activefilters && activefilters.length > 0) {
        for (const filter of activefilters) {
            const filterObj = findFilterInFiltersBySection(filter.name, fulllist);
            if (filterObj) {
                hafs.push({
                    name: filter.name,
                    area: filterObj.area,
                    areaIndex: filterObj.areaIndex,
                    value: filter.value,
                    source: filterObj.source,
                });
            }
        }
    }
    return hafs;
};

// Helper function to get filter sections with counters
const getFilterSectionsWithCounter = (sections, activefilters) => {
    if (!activefilters || activefilters.length === 0) {
        return sections.map((section) => {
            return {
                ...section,
                counter: 0,
            };
        });
    }
    const sectionsWithCounters = sections.map((section) => {
        const sectionFilters = activefilters.filter((filter) => filter.area === section.areaID);
        return {
            ...section,
            counter: sectionFilters.length,
        };
    });
    return sectionsWithCounters;
};

const eventsSearchReducer = (state, action) => {
    const draft = { ...state };

    switch (action.type) {
        case 'SetSort':
            draft.sort = action.sort;
            return draft;

        case 'SetGroupByProject':
            draft.groupByProject = action.groupByProject;
            return draft;

        case 'SetShowTransactions':
            draft.showTransactions = action.showTransactions;
            return draft;

        case 'SetNameFilter':
            draft.nameFilter = action.nameFilter;
            return draft;

        case 'SetEventFilters':
            draft.eventFilters = action.eventFilters;
            return draft;

        case 'SetLastUsedFilters':
            draft.lastUsedFilters = action.filters;
            return draft;

        case 'ToggleEventType':
            draft.eventFilters = { ...draft.eventFilters };
            if (action.enabled) {
                // Add event type with default config
                draft.eventFilters[action.eventType] = { eventSubtypeCode: null };
            } else {
                // Remove event type
                delete draft.eventFilters[action.eventType];
            }
            return draft;

        case 'UpdateEventTypeConfig':
            draft.eventFilters = {
                ...draft.eventFilters,
                [action.eventType]: {
                    ...draft.eventFilters[action.eventType],
                    ...action.config,
                },
            };
            return draft;

        case 'UpdateFilterField':
            draft.eventFilters = {
                ...draft.eventFilters,
                [action.field]: action.value,
            };
            return draft;

        case 'SetActiveTransactionCollapse':
            // Structure: { [projectId]: ['1', '2', ...] }
            // Each project maintains its own array of open panels
            if (!draft.activeTransactionCollapse) {
                draft.activeTransactionCollapse = {};
            }

            const projectId = String(action.projectId);
            const currentKeys = draft.activeTransactionCollapse[projectId] || [];

            let newKeys;
            if (action.collapseKey === null) {
                // Clear all for this project
                newKeys = [];
            } else if (currentKeys.includes(action.collapseKey)) {
                // Remove key (close panel)
                newKeys = currentKeys.filter((k) => k !== action.collapseKey);
            } else {
                // Add key (open panel)
                newKeys = [...currentKeys, action.collapseKey];
            }

            if (newKeys.length > 0) {
                draft.activeTransactionCollapse[projectId] = newKeys;
            } else {
                delete draft.activeTransactionCollapse[projectId];
            }

            return draft;

        case 'ResetFilters':
            draft.eventFilters = action.eventFilters || {};
            draft.nameFilter = '';
            draft.activeTransactionCollapse = {};
            return draft;

        // Smart list management actions
        case 'ChangeActiveSet':
            const newset = draft.sets.find((item) => item.id.toString() === action.activeSet.toString());
            if (!newset) return draft;

            const clonedNewSet = _.cloneDeep(newset);
            const filtersToSet = formatEventFilterValues(clonedNewSet.filters, draft.currentUserID);
            const hydratedFiltersToSet = getHydratedActiveFilters(filtersToSet, draft.filtersBySection);

            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                activeSet: action.activeSet,
                filterType: action.filterType || 'SAVED_SEARCH',
            });

            return {
                ...draft,
                activeSet: action.activeSet,
                activeSetName: clonedNewSet.name,
                activeSetFixed: clonedNewSet.fixed,
                filterType: action.filterType || 'SAVED_SEARCH',
                workingFilters: [...filtersToSet],
                hydratedFilters: hydratedFiltersToSet,
            };

        case 'AddNewSet':
            const customSetCopy = [...draft.sets].map((item) => {
                if (item.id === draft.activeSet) {
                    return {
                        ...item,
                        filters: [...item.originalFilters],
                        unsaved: false,
                    };
                }
                return item;
            });
            const newid = `sl-${Date.now().toString()}`;
            customSetCopy.push({
                id: newid,
                name: action.name,
                filters: [...draft.workingFilters],
                originalFilters: [...draft.workingFilters],
                fixed: false,
                unsaved: false,
            });
            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                activeSet: newid,
                customSets: customSetCopy.filter((s) => !s.fixed),
            });
            saveEventsSmartList(customSetCopy);

            return {
                ...draft,
                sets: customSetCopy,
                activeSet: newid,
                activeSetName: action.name,
                activeSetFixed: false,
            };

        case 'EditCustomSet':
            // Mantenere l'ID originale invece di crearne uno nuovo
            const editSetCopy = [...draft.sets].map((item) => {
                if (item.id === action.newSet.id) {
                    return {
                        ...item,
                        ...action.newSet,
                        // Mantenere l'ID originale
                        id: item.id,
                    };
                }
                return item;
            });

            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                activeSet: action.newSet.id,
                customSets: editSetCopy.filter((s) => !s.fixed),
            });
            saveEventsSmartList(editSetCopy);

            return {
                ...draft,
                sets: editSetCopy,
                activeSet: action.newSet.id,
                activeSetName: action.newSet.name,
            };

        case 'CloneSet':
            const copyOfActive = _.cloneDeep(action.activeSetData);
            copyOfActive.id = Date.now();
            copyOfActive.fixed = false;
            copyOfActive.name = copyOfActive.name + ' (copy)';
            const copyOfSets = [...draft.sets];
            copyOfSets.push(copyOfActive);

            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                activeSet: copyOfActive.id,
                customSets: copyOfSets.filter((s) => !s.fixed),
            });
            saveEventsSmartList(copyOfSets);

            return {
                ...draft,
                sets: copyOfSets,
                activeSet: copyOfActive.id,
                activeSetName: copyOfActive.name,
            };

        case 'DeleteSet':
            const filteredSets = draft.sets.filter((el) => el.id !== action.setID);
            const defaultSet = draft.sets.find((s) => s.fixed) || draft.sets[0];

            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                activeSet: defaultSet.id,
                customSets: filteredSets.filter((s) => !s.fixed),
            });
            saveEventsSmartList(filteredSets);

            const defaultFiltersToActive = formatEventFilterValues([...defaultSet.filters], draft.currentUserID);
            const defaultHydratedFilters = getHydratedActiveFilters(defaultFiltersToActive, draft.filtersBySection);

            return {
                ...draft,
                sets: filteredSets,
                activeSet: defaultSet.id,
                activeSetName: defaultSet.name,
                activeSetFixed: defaultSet.fixed,
                workingFilters: defaultFiltersToActive,
                hydratedFilters: defaultHydratedFilters,
            };

        case 'SaveCustomSets':
            const updatedSets = action.customSets;
            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                customSets: updatedSets.filter((s) => !s.fixed),
            });
            saveEventsSmartList(updatedSets);

            return {
                ...draft,
                sets: updatedSets,
            };

        case 'ChangeFilters':
            const changedFiltersToSet = formatEventFilterValues(action.filters, draft.currentUserID);
            return {
                ...draft,
                workingFilters: changedFiltersToSet,
            };

        case 'ApplyWorkingFilters':
            const workingFiltersToActive = getHydratedActiveFilters(draft.workingFilters, draft.filtersBySection);

            const customSetsUpdated = [...draft.sets].map((item) => {
                if (item.id === draft.activeSet) {
                    return {
                        ...item,
                        filters: draft.workingFilters,
                        unsaved: true,
                    };
                }
                return item;
            });
            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                customSets: customSetsUpdated.filter((s) => !s.fixed),
            });

            return {
                ...draft,
                sets: customSetsUpdated,
                hydratedFilters: workingFiltersToActive,
            };

        case 'SaveSetFilters':
            const savedSets = [...draft.sets].map((item) => {
                if (item.id === draft.activeSet) {
                    return {
                        ...item,
                        filters: [...draft.workingFilters],
                        originalFilters: [...draft.workingFilters],
                        unsaved: false,
                    };
                }
                return item;
            });
            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                customSets: savedSets.filter((s) => !s.fixed),
            });
            saveEventsSmartList(savedSets);

            return {
                ...draft,
                sets: savedSets,
            };

        case 'RenameSet':
            const renamedSets = [...draft.sets].map((item) => {
                if (item.id === draft.activeSet) {
                    return {
                        ...item,
                        name: action.newName,
                    };
                }
                return item;
            });
            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                customSets: renamedSets.filter((s) => !s.fixed),
            });
            saveEventsSmartList(renamedSets);

            return {
                ...draft,
                sets: renamedSets,
                activeSetName: action.newName,
            };

        case 'RevertChanges':
            const currentSetOriginalFilters =
                [...draft.sets].find((item) => item.id === draft.activeSet)?.originalFilters || [];
            const originalFiltersToActive = getHydratedActiveFilters(
                [...currentSetOriginalFilters],
                draft.filtersBySection
            );

            const revertedSets = [...draft.sets].map((item) => {
                if (item.id === draft.activeSet) {
                    return {
                        ...item,
                        filters: [...currentSetOriginalFilters],
                        unsaved: false,
                    };
                }
                return item;
            });
            ls.set('nw_config_eventwallfilter', {
                ...draft.savedConfig,
                customSets: revertedSets.filter((s) => !s.fixed),
            });

            return {
                ...draft,
                sets: revertedSets,
                workingFilters: [...currentSetOriginalFilters],
                hydratedFilters: originalFiltersToActive,
            };

        default:
            return state;
    }
};

const syncToLocalStorage = (storageKey, state) => {
    if (!storageKey) return;

    try {
        const dataToStore = {
            sort: state.sort,
            groupByProject: state.groupByProject,
            showTransactions: state.showTransactions,
        };

        // Only save eventFilters if not in drawer mode
        if (!state.isDrawerMode) {
            dataToStore.eventFilters = state.eventFilters;
        }

        localStorage.setItem(storageKey, JSON.stringify(dataToStore));
    } catch (error) {
        console.error('Error saving to localStorage:', error);
    }
};

const loadFromLocalStorage = (storageKey) => {
    if (!storageKey) return null;

    try {
        const stored = localStorage.getItem(storageKey);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        console.error('Error loading from localStorage:', error);
        return null;
    }
};

// Helper function to create filter sections from EVENTS_FILTERS
const createFilterSections = () => {
    return EVENTS_FILTERS.sections.map((section, index) => ({
        areaName: section.name,
        areaID: section.name,
        areaIndex: index,
        counter: 0,
    }));
};

// Helper function to create filters by section from EVENTS_FILTERS
const createFiltersBySection = () => {
    const sections = [];
    EVENTS_FILTERS.sections.forEach((section, index) => {
        const sectionFilters = EVENTS_FILTERS.filters.filter((f) => f.area === section.name);
        sections.push({
            areaName: section.name,
            areaID: section.name,
            areaIndex: index,
            areaFilters: sectionFilters,
        });
    });
    return sections;
};

export function EventsSearchProvider({
    children,
    modelId = null,
    clientId = null,
    storageKey = null,
    initialSort = 'time-up',
    initialGroupByProject = true,
    initialShowTransactions = true,
    initialEventFilters = {},
    initialActiveTransactionCollapse = {},
    isDrawerMode = false,
    currentUserID = null,
    enableSmartLists = true,
}) {
    // Try to load from localStorage if storageKey is provided
    const savedState = storageKey ? loadFromLocalStorage(storageKey) : null;

    // Load smart list configuration from localStorage
    const savedSmartListConfig = enableSmartLists ? ls.get('nw_config_eventwallfilter') : null;

    // Load static sets and custom sets
    let sets = [];
    let activeSet = 'v1';
    let filterType = 'SAVED_SEARCH';

    if (enableSmartLists) {
        const { EVENTS_FILTERS_SETS } = require('@configs/Filters/EVENTS_FILTERS_SETS');

        // Start with static sets
        const staticSetsWithOriginals = EVENTS_FILTERS_SETS.staticSets.map((set) => ({
            ...set,
            originalFilters: [...set.filters],
            unsaved: false,
        }));

        // Load custom sets from localStorage config
        const customSets = savedSmartListConfig?.customSets || [];
        const customSetsWithOriginals = customSets.map((set) => ({
            ...set,
            originalFilters: set.originalFilters || [...set.filters],
            unsaved: false,
        }));

        sets = [...customSetsWithOriginals, ...staticSetsWithOriginals];
        activeSet = savedSmartListConfig?.activeSet || EVENTS_FILTERS_SETS.activeSet;
        filterType = savedSmartListConfig?.filterType || 'SAVED_SEARCH';
    }

    // Get the current active set
    const currentSet =
        enableSmartLists && sets.length > 0
            ? sets.find((item) => item.id.toString() === activeSet.toString()) || sets[0]
            : null;

    // Format filter values with user context
    const workingFilters =
        currentSet && currentUserID ? formatEventFilterValues(currentSet.filters, currentUserID) : [];

    // Create filter sections and filtersBySection
    const filtersSections = enableSmartLists ? createFilterSections() : [];
    const filtersBySection = enableSmartLists ? createFiltersBySection() : [];

    // Hydrate active filters
    const hydratedFilters =
        currentSet && enableSmartLists ? getHydratedActiveFilters(workingFilters, filtersBySection) : [];

    const filtersSectionsWithCounter = enableSmartLists
        ? getFilterSectionsWithCounter(filtersSections, hydratedFilters)
        : [];

    const INITIAL_STATE = {
        sort: savedState?.sort || initialSort,
        groupByProject: savedState?.groupByProject ?? initialGroupByProject,
        showTransactions: savedState?.showTransactions ?? initialShowTransactions,
        nameFilter: '',
        // In drawer mode, always use initialEventFilters (don't load from localStorage)
        eventFilters: isDrawerMode ? initialEventFilters : savedState?.eventFilters || initialEventFilters,
        activeTransactionCollapse: initialActiveTransactionCollapse || {},
        isDrawerMode,
        lastUsedFilters: null, // Track the last filters used for API call
        modelId: modelId,
        clientId: clientId,
        // Smart list state
        activeSet: activeSet,
        activeSetName: currentSet?.name || 'all events',
        activeSetFixed: currentSet?.fixed || false,
        filterType: filterType,
        sets: sets,
        workingFilters: workingFilters,
        hydratedFilters: hydratedFilters,
        filtersSections: filtersSectionsWithCounter,
        filtersBySection: filtersBySection,
        currentUserID: currentUserID,
        savedConfig: savedSmartListConfig || {},
        enableSmartLists: enableSmartLists,
    };

    const [data, dispatch] = useReducer(eventsSearchReducer, INITIAL_STATE);

    // Sync to localStorage when state changes
    useEffect(() => {
        if (storageKey) {
            syncToLocalStorage(storageKey, data);
        }
    }, [storageKey, data.sort, data.groupByProject, data.showTransactions, data.eventFilters]);

    return (
        <EventsSearchContext.Provider value={data}>
            <EventsSearchDispatchContext.Provider value={dispatch}>{children}</EventsSearchDispatchContext.Provider>
        </EventsSearchContext.Provider>
    );
}

export function useEventsSearchContext() {
    const context = useContext(EventsSearchContext);
    if (context === null) {
        throw new Error('useEventsSearchContext must be used within EventsSearchProvider');
    }
    return context;
}

export function useEventsSearchContextDispatch() {
    const context = useContext(EventsSearchDispatchContext);
    if (context === null) {
        throw new Error('useEventsSearchContextDispatch must be used within EventsSearchProvider');
    }
    return context;
}
